<template>
  <div class="chip-input-container">
    <label v-if="label" class="chip-input-label">{{ label }}<vue-feather v-if="tooltip" type="info" size="14" class="label-tooltip-icon" v-tooltip="tooltip" /></label>

    <!-- Display chips -->
    <div class="chip-display" :class="{ 'chip-display--disabled': disabled }">
      <div v-for="(value, index) in modelValue" :key="index" class="chip" :class="{ 'chip--locked': isLocked(value) }">
        <vue-feather v-if="isLocked(value)" type="lock" size="12" class="lock-icon" />
        <span class="chip-text">{{ formatValue(value) }}</span>
        <button
          v-if="!disabled && !isLocked(value)"
          type="button"
          class="chip-remove"
          @click="removeChip(index)"
          tabindex="-1"
        >
          <vue-feather type="x" size="14" />
        </button>
      </div>

      <!-- Input field -->
      <input
        v-if="!disabled"
        ref="input"
        v-model="inputValue"
        type="text"
        :inputmode="inputType === 'number' ? 'numeric' : 'text'"
        :placeholder="modelValue.length === 0 ? placeholder : ''"
        class="chip-input-field"
        @keydown.enter.prevent="addChip"
        @keydown.backspace="handleBackspace"
        @blur="onBlur"
      />

      <!-- Empty state -->
      <span v-if="disabled && modelValue.length === 0" class="chip-empty">
        {{ placeholder }}
      </span>
    </div>

    <span v-if="error" class="chip-input-error">{{ error }}</span>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'

export default {
  name: 'ChipInput',
  components: {
    'vue-feather': VueFeather
  },
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Type and press Enter...'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    // Input type: 'text' or 'number'
    inputType: {
      type: String,
      default: 'text',
      validator: (value) => ['text', 'number'].includes(value)
    },
    // For number type: min/max validation
    min: {
      type: Number,
      default: null
    },
    max: {
      type: Number,
      default: null
    },
    // Custom formatter for display (e.g., add "h" suffix for hours)
    formatter: {
      type: Function,
      default: null
    },
    // Custom validator function
    validator: {
      type: Function,
      default: null
    },
    // Allow duplicates
    allowDuplicates: {
      type: Boolean,
      default: false
    },
    // Locked items that cannot be removed (shown with lock icon)
    lockedItems: {
      type: Array,
      default: () => []
    },
    tooltip: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      inputValue: ''
    }
  },
  methods: {
    addChip() {
      const trimmed = this.inputValue.trim()
      if (!trimmed) return

      let value = trimmed

      // Convert to number if inputType is number
      if (this.inputType === 'number') {
        value = Number(trimmed)

        // Validate number
        if (isNaN(value)) {
          this.$emit('error', 'Please enter a valid number')
          return
        }

        // Check min/max
        if (this.min !== null && value < this.min) {
          this.$emit('error', `Value must be at least ${this.min}`)
          return
        }
        if (this.max !== null && value > this.max) {
          this.$emit('error', `Value must be at most ${this.max}`)
          return
        }
      }

      // Check for duplicates
      if (!this.allowDuplicates && this.modelValue.includes(value)) {
        this.$emit('error', 'This value already exists')
        return
      }

      // Custom validator
      if (this.validator && !this.validator(value)) {
        return
      }

      // Add to array
      const newValue = [...this.modelValue, value]
      this.$emit('update:modelValue', newValue)
      this.$emit('change', newValue)
      this.$emit('add', value)

      // Clear input
      this.inputValue = ''
    },
    removeChip(index) {
      const newValue = [...this.modelValue]
      const removed = newValue.splice(index, 1)[0]
      this.$emit('update:modelValue', newValue)
      this.$emit('change', newValue)
      this.$emit('remove', removed)
    },
    handleBackspace(event) {
      // If input is empty and backspace is pressed, remove last non-locked chip
      if (this.inputValue === '' && this.modelValue.length > 0) {
        // Find last non-locked item
        for (let i = this.modelValue.length - 1; i >= 0; i--) {
          if (!this.isLocked(this.modelValue[i])) {
            event.preventDefault()
            this.removeChip(i)
            break
          }
        }
      }
    },
    isLocked(value) {
      return this.lockedItems.includes(value)
    },
    onBlur() {
      // Optionally add chip on blur if there's a value
      // Uncomment if you want this behavior:
      // if (this.inputValue.trim()) {
      //   this.addChip()
      // }
    },
    formatValue(value) {
      if (this.formatter) {
        return this.formatter(value)
      }
      return value
    },
    focus() {
      this.$refs.input?.focus()
    }
  },
  emits: ['update:modelValue', 'change', 'add', 'remove', 'error']
}
</script>

<style scoped>
.chip-input-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.chip-input-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 4px;
}

.label-tooltip-icon {
  color: #9ca3af;
  cursor: help;
  flex-shrink: 0;
}

.chip-display {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  height: 45px;
  padding: 4px 12px;
  background: #FFF;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-sizing: border-box;
  transition: border-color 0.15s ease;
}

.chip-display:focus-within {
  border-color: var(--color-primary, #6a16f0);
  box-shadow: 0 0 0 3px rgba(106, 22, 240, 0.1);
}

.chip-display--disabled {
  background: #fafafa;
  cursor: not-allowed;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #fff;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.chip:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.chip--locked {
  background: #fff;
  color: #374151;
  border-color: #d1d5db;
}

.chip--locked:hover {
  background: #fff;
  border-color: #d1d5db;
}

.lock-icon {
  flex-shrink: 0;
}

.chip-text {
  line-height: 1;
}

.chip-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  transition: color 0.15s ease;
  line-height: 1;
}

.chip-remove:hover {
  color: #374151;
}

.chip-input-field {
  flex: 1;
  min-width: 120px;
  height: 100%;
  padding: 0;
  margin: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 1rem;
  font-family: inherit;
  color: #374151;
}

.chip-input-field::placeholder {
  color: #9ca3af;
}

.chip-empty {
  color: #9ca3af;
  font-size: 0.9375rem;
}

.chip-input-error {
  font-size: 0.8125rem;
  color: #dc2626;
}
</style>
